import { useParams, useLocation, Link } from "wouter"
import { Home, ChevronRight, Loader2, Save } from "lucide-react"
import { ValueAssessmentProvider, useValueAssessment } from "@/contexts/ValueAssessmentContext"
import { useProjectDetails } from "@/hooks/useProjectDetails"
import React, { useEffect, useState } from "react"
import redwoodLogoMark from "@assets/Redwood_LogoMark_White.png"

interface ValueAssessmentLayoutProps {
  children: React.ReactNode
}

function ValueAssessmentContent({ children }: ValueAssessmentLayoutProps) {
  const params = useParams()
  const [location, navigate] = useLocation()
  
  // Get projectId from URL params (traditional approach)
  const projectId = params.projectId
  

  
  const { isLoading, isSaving, data } = useValueAssessment()
  const publicToken = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('t') : null
  const isPublic = Boolean(publicToken)
  const { data: project, error: projectError } = useProjectDetails(projectId, !isPublic)
  
  // Handle invalid project ID by redirecting to projects list
  useEffect(() => {
    if (projectError && projectId) {
      console.error(`Project ${projectId} not found, redirecting to projects list`)
      navigate('/')
    }
    // Also handle missing projectId case
    if (!projectId && !location.includes('/start')) {
      console.error("Project start not found, redirecting to projects list")
      navigate('/')
    }
  }, [projectError, projectId, navigate, location])
  
  // Get current step for breadcrumb
  const getCurrentStep = () => {
    if (location.includes('/intro')) return 'Introduction'
    if (location.includes('/kickoff')) return 'Project Kickoff'
    if (location.includes('/context')) return 'Strategic Context'
    if (location.includes('/configuration')) return 'Platform Configuration'
    if (location.includes('/modeling')) return 'Value Modeling'
    if (location.includes('/summary') || location.includes('/value-assessment-summary')) return 'Summary & Results'
    return 'Value Assessment'
  }
  
  // Get client name - prioritize actual project data over assessment form data
  const getClientName = () => {
    // Always prioritize the actual project client_org from database
    if (project?.client_org) return project.client_org
    if (project?.name) return project.name
    // Only fallback to assessment form data if no project data available
    if (data?.kickoff?.clientName) return data.kickoff.clientName
    return 'Client Project'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-forestBlack via-[#04414D] to-[#334155] text-foreground relative">
      {/* Autosave Status Indicator */}
      {(isLoading || isSaving) && (
        <div className="fixed top-4 right-4 z-50 bg-card border border-border rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 text-[#6B9FED] animate-spin" />
              <span className="text-[#6B9FED] text-sm font-medium">Loading...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4 text-[#4AE2C9]" />
              <span className="text-[#4AE2C9] text-sm font-medium">Auto-saving...</span>
            </>
          )}
        </div>
      )}
      
      <div className="container mx-auto px-6 py-8">
        {/* Header with breadcrumb and logo */}
        <div className="flex justify-between items-start mb-6">
          {/* Custom breadcrumb for Value Assessment flow */}
          <nav className="text-sm text-gray-400 flex gap-2 items-center">
            <Link to="/" className="hover:text-[#4AE2C9] transition-colors flex items-center">
              <Home className="h-4 w-4" />
            </Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/projects" className="hover:text-[#4AE2C9] transition-colors">
              Projects
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="hover:text-[#4AE2C9] transition-colors">{getClientName()}</span>
            <ChevronRight className="h-4 w-4" />
            <span className="breadcrumb-active">{getCurrentStep()}</span>
          </nav>
          
          {/* Redwood Logo Mark */}
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            <img src={redwoodLogoMark} alt="Redwood Logo Mark" className="h-8 w-8" />
          </div>
        </div>
        </div>
        
        <div className="max-w-5xl mx-auto">
          {children}
        </div>
      </div>
    </div>
  )
}

export default function ValueAssessmentLayout({ children }: ValueAssessmentLayoutProps) {
  return (
    <ValueAssessmentProvider>
      <ValueAssessmentContent>
        {children}
      </ValueAssessmentContent>
    </ValueAssessmentProvider>
  )
}
